import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { num } from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle, RotateCcw, Wand2, Play, Database,
} from "lucide-react";

function parseCSV(text) {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return { headers: [], rows: [] };
  const headers = lines[0].split(",").map((h) => h.trim());
  const rows = lines.slice(1).map((line) => {
    const cells = line.split(",");
    const obj = {};
    headers.forEach((h, i) => { obj[h] = (cells[i] ?? "").trim(); });
    return obj;
  });
  return { headers, rows };
}

const SAMPLES = {
  targets: "dealer_code,year,month,target_mt\nDLR0001,2026,4,120\nDLR0002,2026,4,80\nDLR0003,2026,4,60\nDLR0004,2026,4,150",
  sales: "invoice_no,dealer_code,sku_code,qty_mt,amount,txn_date\nUPL-0001,DLR0001,SKU001,25.5,142800,2026-04-05\nUPL-0002,DLR0002,SKU002,12.0,67200,2026-04-06\nUPL-0003,DLR0003,SKU003,40.0,232000,2026-04-08",
  dealers: "dealer_code,dealer_name,opco_code,region,rso,ao,status\nNEWDLR100,New Dealer 100,ACC,South,South RSO-1,AO-01,active\nNEWDLR101,New Dealer 101,ACC,South,South RSO-1,AO-02,new",
  retailers: "retailer_code,retailer_name,dealer_code\nRETNEW01,New Retailer 1,DLR0001\nRETNEW02,New Retailer 2,DLR0002",
};

const COMMIT_TARGET = { targets: "targets", sales: "invoices", dealers: "dealers", retailers: "retailers" };

export default function UploadStudio() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canEdit = ["Super Administrator", "Data Operations", "Corporate Scheme Admin", "OpCo Scheme Manager"].includes(user?.role);
  const [type, setType] = useState("targets");
  const [parsed, setParsed] = useState(null);
  const [mapping, setMapping] = useState({});
  const [filename, setFilename] = useState("");
  const [batchId, setBatchId] = useState(null);
  const [validation, setValidation] = useState(null);

  const { data: templates } = useQuery({
    queryKey: ["upload-templates"],
    queryFn: async () => (await api.get("/upload/templates")).data,
  });
  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ["upload-batches"],
    queryFn: async () => (await api.get("/upload/batches")).data,
  });

  const fields = templates?.templates?.[type]?.fields || [];

  const autoMap = (headers) => {
    const m = {};
    fields.forEach((f) => {
      const hit = headers.find((h) => h.toLowerCase().replace(/[^a-z0-9]/g, "") === f.key.toLowerCase().replace(/[^a-z0-9]/g, ""));
      if (hit) m[f.key] = hit;
    });
    return m;
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const p = parseCSV(text);
    setParsed(p); setFilename(file.name); setMapping(autoMap(p.headers));
    setBatchId(null); setValidation(null);
  };

  const loadSample = () => {
    const p = parseCSV(SAMPLES[type]);
    setParsed(p); setFilename(`sample_${type}.csv`); setMapping(autoMap(p.headers));
    setBatchId(null); setValidation(null);
    toast.success("Sample data loaded");
  };

  const resetType = (t) => { setType(t); setParsed(null); setMapping({}); setBatchId(null); setValidation(null); setFilename(""); };

  const validate = useMutation({
    mutationFn: async () => {
      let bid = batchId;
      if (!bid) {
        const created = (await api.post("/upload/batches", {
          type, filename, headers: parsed.headers, rows: parsed.rows, mapping,
        })).data;
        bid = created.id; setBatchId(bid);
      }
      return { bid, res: (await api.post(`/upload/batches/${bid}/validate`, { mapping })).data };
    },
    onSuccess: ({ res }) => { setValidation(res); qc.invalidateQueries({ queryKey: ["upload-batches"] });
      res.can_commit ? toast.success(`Validation passed: ${res.valid} rows ready`) : toast.warning(`${res.invalid} invalid rows found`); },
    onError: (e) => toast.error(e.response?.data?.detail || "Validation failed"),
  });

  const commit = useMutation({
    mutationFn: async () => (await api.post(`/upload/batches/${batchId}/commit`)).data,
    onSuccess: (r) => { toast.success(`Committed ${r.inserted} rows`); qc.invalidateQueries({ queryKey: ["upload-batches"] });
      setParsed(null); setBatchId(null); setValidation(null); setFilename(""); },
    onError: (e) => toast.error(e.response?.data?.detail || "Commit failed"),
  });

  const rollback = useMutation({
    mutationFn: async (bid) => (await api.post(`/upload/batches/${bid}/rollback`)).data,
    onSuccess: (r) => { toast.success(`Rolled back ${r.removed} rows`); qc.invalidateQueries({ queryKey: ["upload-batches"] }); },
    onError: (e) => toast.error(e.response?.data?.detail || "Rollback failed"),
  });

  return (
    <div>
      <PageHeader title="Upload & Mapping Studio" subtitle="Upload sales/target files, map columns, validate and roll back bad batches" />

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_420px] gap-6">
        <div className="space-y-6">
          <Panel testid="upload-config-panel" title="1 · Choose data type & upload">
            <div className="flex gap-2 mb-4">
              {Object.entries(templates?.templates || {}).map(([k, v]) => (
                <button key={k} data-testid={`upload-type-${k}`} onClick={() => resetType(k)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${type === k ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-slate-600 border-slate-200"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center">
              <UploadCloud className="mx-auto text-slate-300" size={40} />
              <p className="text-sm text-slate-500 mt-2">Upload a CSV file with headers in the first row</p>
              <div className="flex items-center justify-center gap-3 mt-4">
                {canEdit ? (
                  <>
                    <label className="cursor-pointer">
                      <input type="file" accept=".csv,text/csv" className="hidden" data-testid="upload-file-input" onChange={handleFile} />
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#00529B] text-white text-sm font-medium hover:bg-[#0F2C59]">
                        <FileSpreadsheet size={16} /> Select CSV
                      </span>
                    </label>
                    <Button variant="outline" className="gap-2" data-testid="load-sample-btn" onClick={loadSample}>
                      <Wand2 size={15} /> Load sample
                    </Button>
                  </>
                ) : (
                  <span className="text-sm text-slate-400">Read-only role — uploads are disabled.</span>
                )}
              </div>
              {filename && <div className="mt-3 text-xs text-slate-500">Loaded: <b>{filename}</b> · {parsed?.rows?.length || 0} rows</div>}
            </div>
          </Panel>

          {parsed && (
            <Panel testid="upload-mapping-panel" title="2 · Map columns" subtitle="Match your file headers to the required target fields">
              <div className="space-y-3">
                {fields.map((f) => (
                  <div key={f.key} className="grid grid-cols-2 gap-3 items-center" data-testid={`map-field-${f.key}`}>
                    <div className="text-sm">
                      <span className="font-medium text-slate-800">{f.label}</span>
                      {f.required && <span className="text-red-500 ml-1">*</span>}
                      <span className="text-xs text-slate-400 ml-2">({f.type})</span>
                    </div>
                    <select className="h-10 rounded-md border border-slate-200 px-3 bg-white text-sm"
                      data-testid={`map-select-${f.key}`} value={mapping[f.key] || ""}
                      onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}>
                      <option value="">— not mapped —</option>
                      {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <Button className="gap-2 mt-5 bg-[#00529B] hover:bg-[#0F2C59]" data-testid="validate-btn"
                disabled={validate.isPending} onClick={() => validate.mutate()}>
                <Play size={15} /> {validate.isPending ? "Validating…" : "Validate"}
              </Button>
            </Panel>
          )}

          {validation && (
            <Panel testid="upload-validation-panel" title="3 · Validation results">
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="rounded-lg border border-slate-200 p-3 text-center">
                  <div className="text-2xl font-heading font-bold text-slate-800">{num(validation.total)}</div>
                  <div className="text-xs text-slate-400 uppercase tracking-wider">Total</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
                  <div className="text-2xl font-heading font-bold text-emerald-700">{num(validation.valid)}</div>
                  <div className="text-xs text-emerald-600 uppercase tracking-wider">Valid</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                  <div className="text-2xl font-heading font-bold text-red-700">{num(validation.invalid)}</div>
                  <div className="text-xs text-red-600 uppercase tracking-wider">Invalid</div>
                </div>
              </div>
              {validation.errors?.length > 0 && (
                <div className="max-h-52 overflow-y-auto border border-slate-200 rounded-lg mb-4">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-left text-slate-400"><th className="py-1.5 px-2">Row</th><th className="py-1.5 px-2">Issues</th></tr>
                    </thead>
                    <tbody>
                      {validation.errors.map((er) => (
                        <tr key={er.row} className="border-t border-slate-50">
                          <td className="py-1.5 px-2 font-mono text-slate-500">{er.row}</td>
                          <td className="py-1.5 px-2 text-red-600">{er.messages.join("; ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {validation.can_commit ? (
                <div className="flex items-center gap-3">
                  <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="commit-btn"
                    disabled={commit.isPending || !canEdit} onClick={() => commit.mutate()}>
                    <CheckCircle2 size={16} /> Commit {num(validation.valid)} rows
                  </Button>
                  <span className="text-xs text-slate-400">Writes to {COMMIT_TARGET[type]} · reversible via rollback</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <AlertTriangle size={16} /> Fix all invalid rows before committing — a batch cannot be committed until validation passes with zero errors.
                </div>
              )}
            </Panel>
          )}
        </div>

        {/* Batch history */}
        <Panel testid="batch-history-panel" title="Batch History" subtitle="Idempotent uploads · rollback support">
          {batchesLoading ? <Loading /> : (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto">
              {batches?.batches?.length ? batches.batches.map((b) => (
                <div key={b.id} className="rounded-xl border border-slate-200 p-3" data-testid={`batch-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-slate-800 text-sm truncate">{b.filename}</span>
                    <Pill tone={b.status === "committed" ? "settled" : b.status === "rolled_back" ? "inactive" : b.status === "validated" ? "new" : "draft"}>
                      {b.status.replace("_", " ")}
                    </Pill>
                  </div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-400">
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 capitalize">{b.type}</span>
                    <span>{num(b.row_count)} rows</span>
                    {b.inserted_count != null && <span className="text-emerald-600">{num(b.inserted_count)} inserted</span>}
                  </div>
                  {b.status === "committed" && canEdit && (
                    <Button size="sm" variant="outline" className="gap-1.5 mt-2 text-red-600 border-red-200" data-testid={`rollback-btn-${b.id}`}
                      disabled={rollback.isPending} onClick={() => rollback.mutate(b.id)}>
                      <RotateCcw size={13} /> Rollback
                    </Button>
                  )}
                </div>
              )) : (
                <div className="text-center py-12 text-slate-400 text-sm">
                  <Database className="mx-auto mb-2 text-slate-300" size={28} /> No batches yet
                </div>
              )}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
